import {gql} from '@apollo/client';

const reportPackFormFragment = gql`
  fragment ReportPackFormFragment on ReportPackFormType {
    address
    adviceReceived
    bwPlanNumber
    childAddress
    childDob
    childForenames
    childNiNumber
    childPostcode
    childSurname
    childTitle
    contact {
      id
    }
    contactCuroId
    contributionAmount
    contributionFrequencyType
    contributionPaymentDateType
    contributionSourceType
    contributionType
    createdBySystem
    dateCreated
    dateDeleted
    dateUpdated
    dob
    forenames
    formTransferType
    formTransferTypeLabel
    formType
    formTypeLabel
    furtherActionRequiredMessage
    id
    isaAmount
    isaFormSubscriptionType
    isaFormSubscriptionTypeLabel
    isaManagerAddress1
    isaManagerAddress2
    isaManagerCity
    isaManagerName
    isaManagerPlanRef
    isaManagerPostcode
    isaManagerSortCode
    isaTransferAmount
    isaTransferType
    isDeleted
    isDummy
    jisaFormSubscriptionType
    jisaFormSubscriptionTypeLabel
    managerAddress1
    managerAddress2
    managerCity
    managerCompanyName
    managerPhoneNumber
    managerPostcode
    managerTradingName
    niNumber
    nomineeFormTransferType
    nomineeFormTransferTypeLabel
    postcode
    providerAccountNumber
    reportPackId
    sippNumber
    sippTransferApproximateValue
    sippTransferAssetsInSpecie
    sippTransferContinueIncome
    sippTransferDisqualifyingCredits
    sippTransferDrawdownType
    sippTransferFlexiAccessConvert
    sippTransferFullValue
    sippTransferPlanRef
    sippTransferSchemeType
    statusAndMissing {
      status
      missingFields
    }
    surname
    taxYear
    title
    version
  }
`;

export const reportPackContactFragment = gql`
  fragment ReportPackContactFragment on ReportPackContactType {
    id
    fullName
    signedStatus
    dateSigned
    chasers {
      id
      title
      dateCreated
    }
  }
`;

const reportPackFragment = gql`
  ${reportPackFormFragment}
  ${reportPackContactFragment}
  fragment ReportPackFragment on ReportPackType {
    id
    clientPortalId
    linkedClientPortalId
    message
    documentFilename
    documentClientPortalId
    title
    status
    templateHtml
    isEditable
    templateType
    templateUploadDate
    forms {
      ...ReportPackFormFragment
    }
    documentUploadDate
    documentTitle
    availableSmartflowTemplates {
      id
      name
      tags
    }
    # contactId
    reviewId
    contactCuroIds
    genericDocuments {
      id
    }
    isSignedInClientPortal
    dateSignedInClientPortal
    isLinkedSignedInClientPortal
    dateLinkedSignedInClientPortal
    smartflowExecution {
      id
      flowId
      entity
      name
      executionId
      isExecuted
      isComplete
      status
    }
    contacts {
      ...ReportPackContactFragment
    }
    templateType
    templateHtml
    templateNewOrExistingClient
    templateAtrType
    templateDocumentTypes
    templateMonths
  }
`;

export const reportPackQuery = gql`
  query ReportPackQuery($reviewId: UUID!) {
    getReportPackByReview(reviewId: $reviewId) {
      ...ReportPackFragment
    }
  }
  ${reportPackFragment}
`;

export const getNoActionLetterHtmlQuery = gql`
  query GetNoActionLetterHtml(
    $reportPackId: UUID!
    $formType: ReportPackTemplateTypes!
    $newOrExisting: ReportPackTemplateNewOrExistingClientEnum!
    $atrType: ReportPackTemplateATRTypeEnum!
    $documentTypes: [ReportPackTemplateDocumentTypeEnum]
  ) {
    getNoActionLetterHtml(
      reportPackId: $reportPackId
      formType: $formType
      newOrExisting: $newOrExisting
      atrType: $atrType
      documentTypes: $documentTypes
    )
  }
`;

export const getDeclinedLetterHtmlQuery = gql`
  query GetDeclinedLetterHtml(
    $reportPackId: UUID!
    $formType: ReportPackTemplateTypes!
    $months: ReportPackTemplateMonthsEnum!
    $atrType: ReportPackTemplateATRTypeEnum!
  ) {
    getDeclinedLetterHtml(
      reportPackId: $reportPackId
      formType: $formType
      months: $months
      atrType: $atrType
    )
  }
`;

export const getUncontactableLetterHtmlQuery = gql`
  query GetUncontactableLetterHtml(
    $reportPackId: UUID!
    $formType: ReportPackTemplateTypes!
    $months: ReportPackTemplateMonthsEnum!
    $atrType: ReportPackTemplateATRTypeEnum!
  ) {
    getUncontactableLetterHtml(
      reportPackId: $reportPackId
      formType: $formType
      months: $months
      atrType: $atrType
    )
  }
`;

export const getGenericDocumentQuery = gql`
  query GetGenericDocuments {
    getGenericDocuments {
      id
      title
      version
      current
    }
  }
`;

export const resendReportPackEmail = gql`
  mutation ResendReportPackEmail($reportPackId: UUID!, $contactCuroId: UUID!) {
    resendReportPackEmail(reportPackId: $reportPackId, contactCuroId: $contactCuroId) {
      ok
      errors
    }
  }
`;

export const upsertReportPack = gql`
  mutation UpsertReportPack(
    $message: String
    $reportPackId: UUID
    $reviewId: UUID!
    $title: String
    $contacts: [ReportPackContactInput!]
    $genericDocumentIds: [UUID!]
    $templateHtml: String
    $templateType: ReportPackTemplateTypes
    $templateAtrType: ReportPackTemplateATRTypeEnum
    $templateDocumentTypes: [ReportPackTemplateDocumentTypeEnum!]
    $templateMonths: ReportPackTemplateMonthsEnum
    $templateNewOrExistingClient: ReportPackTemplateNewOrExistingClientEnum
  ) {
    upsertReportPack(
      contacts: $contacts
      message: $message
      reportPackId: $reportPackId
      reviewId: $reviewId
      title: $title
      genericDocumentIds: $genericDocumentIds
      templateHtml: $templateHtml
      templateType: $templateType
      templateAtrType: $templateAtrType
      templateDocumentTypes: $templateDocumentTypes
      templateMonths: $templateMonths
      templateNewOrExistingClient: $templateNewOrExistingClient
    ) {
      ok
      errors
      reportPack {
        ...ReportPackFragment
      }
    }
  }
  ${reportPackFragment}
`;

export const submitReportPack = gql`
  mutation SubmitReportPack($reportPackId: UUID!) {
    submitReportPack(reportPackId: $reportPackId) {
      ok
      reportPack {
        ...ReportPackFragment
      }
      errors
    }
  }
  ${reportPackFragment}
`;

export const cancelReportPack = gql`
  mutation CancelReportPack($reportPackId: UUID!) {
    cancelReportPack(reportPackId: $reportPackId) {
      ok
      errors
    }
  }
`;

export const deleteReportPack = gql`
  mutation DeleteReportPack($reportPackId: UUID!) {
    deleteReportPack(reportPackId: $reportPackId) {
      ok
      errors
    }
  }
`;

export const amendReportPack = gql`
  mutation AmendReportPack($reportPackId: UUID!) {
    amendReportPack(reportPackId: $reportPackId) {
      ok
      errors
    }
  }
`;

export const uploadDocumentToReportPack = gql`
  mutation UploadDocumentToReportPack(
    $fileData: String!
    $fileName: String!
    $reportPackId: UUID!
  ) {
    uploadDocumentToReportPack(
      fileData: $fileData
      fileName: $fileName
      reportPackId: $reportPackId
    ) {
      ok
      errors
      reportPack {
        ...ReportPackFragment
      }
    }
  }
  ${reportPackFragment}
`;

export const upsertIsaTransferForm = gql`
  mutation UpsertIsaTransferForm(
    $formId: UUID
    $isaManagerAddress1: String
    $isaManagerAddress2: String
    $isaManagerName: String
    $isaManagerPostcode: String
    $isaManagerPlanRef: String
    $isaManagerCity: String
    $reportPackId: UUID!
    $isaManagerSortCode: String
    $contactCuroId: UUID!
    $formTransferType: FormTransferTypes
    $isaTransferAmount: Decimal
    $isaTransferType: IsaTransferTypes
  ) {
    upsertIsaTransferForm(
      formId: $formId
      isaManagerAddress1: $isaManagerAddress1
      isaManagerAddress2: $isaManagerAddress2
      isaManagerName: $isaManagerName
      isaManagerPostcode: $isaManagerPostcode
      isaManagerSortCode: $isaManagerSortCode
      isaManagerPlanRef: $isaManagerPlanRef
      isaManagerCity: $isaManagerCity
      reportPackId: $reportPackId
      formTransferType: $formTransferType
      contactCuroId: $contactCuroId
      isaTransferAmount: $isaTransferAmount
      isaTransferType: $isaTransferType
    ) {
      ok
      errors
      form {
        ...ReportPackFormFragment
      }
    }
  }

  ${reportPackFormFragment}
`;

export const upsertJISATransferForm = gql`
  mutation UpsertJisaTransferForm(
    $childDob: DateTime
    $childForenames: String
    $childNiNumber: String
    $childSurname: String
    $childPostcode: String
    $childAddress: String
    $childTitle: String
    $formId: UUID
    $isaManagerAddress1: String
    $isaManagerAddress2: String
    $isaManagerName: String
    $isaManagerPostcode: String
    $isaManagerPlanRef: String
    $isaManagerSortCode: String
    $isaManagerCity: String
    $reportPackId: UUID!
    $contactCuroId: UUID!
    $formTransferType: FormTransferTypes
  ) {
    upsertJisaTransferForm(
      childDob: $childDob
      childForenames: $childForenames
      childNiNumber: $childNiNumber
      childSurname: $childSurname
      childPostcode: $childPostcode
      childAddress: $childAddress
      childTitle: $childTitle
      formId: $formId
      isaManagerAddress1: $isaManagerAddress1
      isaManagerAddress2: $isaManagerAddress2
      isaManagerName: $isaManagerName
      isaManagerPostcode: $isaManagerPostcode
      isaManagerPlanRef: $isaManagerPlanRef
      isaManagerCity: $isaManagerCity
      reportPackId: $reportPackId
      formTransferType: $formTransferType
      isaManagerSortCode: $isaManagerSortCode

      contactCuroId: $contactCuroId
    ) {
      ok
      errors
      form {
        ...ReportPackFormFragment
      }
    }
  }

  ${reportPackFormFragment}
`;

export const deleteReportPackForm = gql`
  mutation DeleteReportPackForm($formId: UUID!) {
    deleteReportPackForm(formId: $formId) {
      ok
      errors
    }
  }
`;

export const upsertIsaApplicationForm = gql`
  mutation UpsertIsaApplicationForm(
    $formId: UUID
    $reportPackId: UUID!
    $contactCuroId: UUID!
    $isaFormSubscriptionType: ISAFormSubscriptionTypes
    $taxYear: String
    $isaAmount: Decimal
  ) {
    upsertIsaApplicationForm(
      formId: $formId
      reportPackId: $reportPackId
      isaFormSubscriptionType: $isaFormSubscriptionType
      contactCuroId: $contactCuroId
      taxYear: $taxYear
      isaAmount: $isaAmount
    ) {
      ok
      errors
      form {
        ...ReportPackFormFragment
      }
    }
  }

  ${reportPackFormFragment}
`;

export const upsertNomineeTransferForm = gql`
  mutation UpsertNomineeTransferForm(
    $formId: UUID
    $reportPackId: UUID!
    $contactCuroId: UUID!
    $nomineeFormTransferType: NomineeTransferFormTypes
    $providerAccountNumber: String
    $managerCompanyName: String
    $managerTradingName: String
    $managerAddress1: String
    $managerAddress2: String
    $managerCity: String
    $managerPostcode: String
    $managerPhoneNumber: String
  ) {
    upsertNomineeTransferForm(
      formId: $formId
      reportPackId: $reportPackId
      nomineeFormTransferType: $nomineeFormTransferType
      contactCuroId: $contactCuroId
      providerAccountNumber: $providerAccountNumber
      managerCompanyName: $managerCompanyName
      managerTradingName: $managerTradingName
      managerAddress1: $managerAddress1
      managerAddress2: $managerAddress2
      managerCity: $managerCity
      managerPostcode: $managerPostcode
      managerPhoneNumber: $managerPhoneNumber
    ) {
      ok
      errors
      form {
        ...ReportPackFormFragment
      }
    }
  }

  ${reportPackFormFragment}
`;

export const upsertJisaApplicationForm = gql`
  mutation UpsertJisaApplicationForm(
    $formId: UUID
    $reportPackId: UUID!
    $contactCuroId: UUID!
    $jisaFormSubscriptionType: JISAFormSubscriptionTypes
    $taxYear: String
    $isaAmount: Decimal
    $childTitle: String
    $childForenames: String
    $childSurname: String
    $childAddress: String
    $childPostcode: String
    $childDob: DateTime
    $childNiNumber: String
  ) {
    upsertJisaApplicationForm(
      formId: $formId
      reportPackId: $reportPackId
      jisaFormSubscriptionType: $jisaFormSubscriptionType
      contactCuroId: $contactCuroId
      taxYear: $taxYear
      isaAmount: $isaAmount
      childTitle: $childTitle
      childForenames: $childForenames
      childSurname: $childSurname
      childAddress: $childAddress
      childPostcode: $childPostcode
      childDob: $childDob
      childNiNumber: $childNiNumber
    ) {
      ok
      errors
      form {
        ...ReportPackFormFragment
      }
    }
  }

  ${reportPackFormFragment}
`;

export const upsertSippContributionForm = gql`
  mutation UpsertSIPPContributionForm(
    $formId: UUID
    $reportPackId: UUID!
    $contactCuroId: UUID!
    $adviceReceived: Boolean
    $sippNumber: String
    $contributionAmount: Decimal
    $contributionFrequencyType: ContributionFrequencyTypes
    $contributionPaymentDateType: ContributionPaymentDateTypes
    $contributionSourceType: ContributionSourceTypes
    $contributionType: ContributionTypes
  ) {
    upsertSippContributionForm(
      formId: $formId
      reportPackId: $reportPackId
      contactCuroId: $contactCuroId
      adviceReceived: $adviceReceived
      sippNumber: $sippNumber
      contributionAmount: $contributionAmount
      contributionFrequencyType: $contributionFrequencyType
      contributionPaymentDateType: $contributionPaymentDateType
      contributionSourceType: $contributionSourceType
      contributionType: $contributionType
    ) {
      ok
      errors
      form {
        ...ReportPackFormFragment
      }
    }
  }

  ${reportPackFormFragment}
`;

export const upsertMultreesDdmForm = gql`
  mutation UpsertMultreesDdmForm(
    $contactCuroId: UUID!
    $formId: UUID
    $reportPackId: UUID!
  ) {
    upsertMultreesDdmForm(
      formId: $formId
      reportPackId: $reportPackId
      contactCuroId: $contactCuroId
    ) {
      ok
      errors
      form {
        ...ReportPackFormFragment
      }
    }
  }

  ${reportPackFormFragment}
`;

export const upsertSippTransferForm = gql`
  mutation UpsertSippTransferForm(
    $contactCuroId: UUID!
    $formId: UUID
    $reportPackId: UUID!
    $managerAddress1: String
    $managerAddress2: String
    $managerCity: String
    $managerCompanyName: String
    $managerPostcode: String
    $sippNumber: String
    $sippTransferApproximateValue: Decimal
    $sippTransferContinueIncome: Boolean
    $sippTransferDisqualifyingCredits: Boolean
    $sippTransferDrawdownType: TransferDrawdownTypes
    $sippTransferFlexiAccessConvert: Boolean
    $sippTransferFullValue: Boolean
    $sippTransferPlanRef: String
    $sippTransferSchemeType: TransferSchemeTypes
    $sippTransferAssetsInSpecie: String
  ) {
    upsertSippTransferForm(
      formId: $formId
      reportPackId: $reportPackId
      contactCuroId: $contactCuroId
      managerAddress1: $managerAddress1
      managerAddress2: $managerAddress2
      managerCompanyName: $managerCompanyName
      managerCity: $managerCity
      managerPostcode: $managerPostcode
      sippNumber: $sippNumber
      sippTransferApproximateValue: $sippTransferApproximateValue
      sippTransferContinueIncome: $sippTransferContinueIncome
      sippTransferDisqualifyingCredits: $sippTransferDisqualifyingCredits
      sippTransferDrawdownType: $sippTransferDrawdownType
      sippTransferFlexiAccessConvert: $sippTransferFlexiAccessConvert
      sippTransferFullValue: $sippTransferFullValue
      sippTransferPlanRef: $sippTransferPlanRef
      sippTransferSchemeType: $sippTransferSchemeType
      sippTransferAssetsInSpecie: $sippTransferAssetsInSpecie
    ) {
      ok
      errors
      form {
        ...ReportPackFormFragment
      }
    }
  }

  ${reportPackFormFragment}
`;

export const upsertSippDdmForm = gql`
  mutation UpsertSippDdmForm(
    $contactCuroId: UUID!
    $formId: UUID
    $reportPackId: UUID!
    $bwPlanNumber: String
  ) {
    upsertSippDdmForm(
      formId: $formId
      reportPackId: $reportPackId
      contactCuroId: $contactCuroId
      bwPlanNumber: $bwPlanNumber
    ) {
      ok
      errors
      form {
        ...ReportPackFormFragment
      }
    }
  }

  ${reportPackFormFragment}
`;

export const uploadGenericDocumentMutation = gql`
  mutation UploadGenericDocument(
    $fileData: String!
    $fileName: String!
    $title: String!
    $version: String!
  ) {
    uploadGenericDocument(
      fileData: $fileData
      fileName: $fileName
      title: $title
      version: $version
    ) {
      ok
      errors
    }
  }
`;

export const uploadExpressionOfWishesFormMutation = gql`
  mutation UpsertExpressionOfWishesForm(
    $contactCuroId: UUID!
    $formId: UUID
    $reportPackId: UUID!
    $sippNumber: String
  ) {
    upsertExpressionOfWishesForm(
      reportPackId: $reportPackId
      contactCuroId: $contactCuroId
      formId: $formId
      sippNumber: $sippNumber
    ) {
      ok
      errors
      form {
        ...ReportPackFormFragment
      }
    }
  }

  ${reportPackFormFragment}
`;

export const uploadFurtherActionRequiredFormMutation = gql`
  mutation UpsertFurtherActionRequiredForm(
    $contactCuroId: UUID!
    $formId: UUID
    $furtherActionRequiredMessage: String
    $reportPackId: UUID!
  ) {
    upsertFurtherActionRequiredForm(
      reportPackId: $reportPackId
      contactCuroId: $contactCuroId
      formId: $formId
      furtherActionRequiredMessage: $furtherActionRequiredMessage
    ) {
      ok
      errors
      form {
        ...ReportPackFormFragment
      }
    }
  }

  ${reportPackFormFragment}
`;

export const uploadBankDetailCollectionFormMutation = gql`
  mutation UpsertBankDetailCollectionForm(
    $contactCuroId: UUID!
    $reportPackId: UUID!
    $formId: UUID
  ) {
    upsertBankDetailCollectionForm(
      reportPackId: $reportPackId
      contactCuroId: $contactCuroId
      formId: $formId
    ) {
      ok
      errors
      form {
        ...ReportPackFormFragment
      }
    }
  }
  ${reportPackFormFragment}
`;

export const markReportPackAsNoLongerValidMutation = gql`
  mutation MarkReportPackAsNoLongerValid($reportPackId: UUID!) {
    markReportPackAsNoLongerValid(reportPackId: $reportPackId) {
      ok
      errors
    }
  }
`;

export const genericDocumentStatusFragment = gql`
  fragment GenericDocumentStatusFragment on DisableGenericDocument {
    ok
    errors
  }
`;

export const DISABLE_GENERIC_DOCUMENT_MUTATION = gql`
  mutation DisableGenericDocument($genericDocumentId: UUID!) {
    disableGenericDocument(genericDocumentId: $genericDocumentId) {
      ...GenericDocumentStatusFragment
    }
  }
  ${genericDocumentStatusFragment}
`;
export const GET_GENERIC_DOCUMENT_URL_QUERY = gql`
  query GetGenericDocumentUrl($genericDocumentId: UUID!) {
    getGenericDocumentUrl(genericDocumentId: $genericDocumentId)
  }
`;

export const uploadSmartflowToReportPack = gql`
  mutation UploadSmartflowToReportPack($smartflowId: UUID!, $reportPackId: UUID!) {
    uploadSmartflowToReportPack(smartflowId: $smartflowId, reportPackId: $reportPackId) {
      ok
      errors
      smartflowExecution {
        id
        flowId
        entity
        name
        executionId
        isExecuted
        isComplete
        status
      }
    }
  }
`;

export const deleteReportPackSmartflowExecution = gql`
  mutation DeleteReportPackSmartflowExecution($reportPackId: UUID!) {
    deleteReportPackSmartflowExecution(reportPackId: $reportPackId) {
      ok
      errors
    }
  }
`;
